import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, LinearProgress } from "@mui/material";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { guilds, savedBranch, saveBranch, canEdit, canView, titleHas, date, JOIN_MODES, roleName, PlayerLink, ConfirmAction } from "./community";

// Evil Tower's guild layer (service `guild`, Docs/Guild.md §10; 0.31): `#guild` reads get / muted
const guildLayer = (description, parts, options) => api.group("guild")(description, parts, options);
const MILESTONES = ["—", "bronze", "silver", "gold"];

// Community → Guilds (service `guilds`, Docs/Social.md §7 + §10): the branch's guilds (`guilds list <branch> [query]`); a guild's name
// opens `guilds get <id>` below the table - the roster, requests, invites, the title's ext - and the operator's verbs: disband,
// rename, transfer (a member becomes leader), kick, set-ext (the title's JSON). `#guilds` views, `guilds` acts. A title with the guild layer
// (Evil Tower, service `guild`) adds its panel to the open guild: level, XP, coins, war coins, the week's bar against its goals, the muted
// (`guild get` / `guild muted`, read-only here: the operator's writes are the CLI's).
export default class GuildsTab extends React.Component {
    state = { branch: savedBranch(), query: "", list: null, loading: false, guild: null, busy: false, action: null, layer: null, muted: null };

    componentDidMount() { this.load(); }

    load = () => {
        const { branch, query } = this.state;
        saveBranch(branch);
        this.setState({ loading: true });
        guilds("Loading the guilds", ["list", branch, ...(query.trim() ? [query.trim()] : [])], { silent: true })
            .then(r => this.setState({ list: Array.isArray(r) ? r : r?.guilds ?? [] }))
            .catch(() => this.setState({ list: [] }))
            .finally(() => this.setState({ loading: false }));
    }

    open = (id) => {
        guilds("Loading the guild", ["get", id, "--branch", this.state.branch], { silent: true })
            .then(r => { if (r && typeof r === "object") this.setState({ guild: r }); })
            .catch(() => {});
        this.setState({ layer: null, muted: null });
        if (!titleHas("guild") || !canView("guild")) return;
        guildLayer("Loading the guild layer", ["get", id, "--branch", this.state.branch], { silent: true })
            .then(r => this.setState({ layer: r && typeof r === "object" ? r : { off: String(r ?? "") } }))
            .catch(() => {});
        guildLayer("Loading the muted", ["muted", id, "--branch", this.state.branch], { silent: true })
            .then(r => this.setState({ muted: r && typeof r === "object" ? r.muted ?? [] : [] }))
            .catch(() => {});
    }

    // Evil Tower's guild layer of the open guild: the numbers, the week bar (waves against the three goals), the muted
    renderLayer() {
        const { layer: x, muted } = this.state;
        if (!x) return null;
        if (x.off !== undefined) return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Guild layer: {x.off || "off"}</Typography>;
        const w = x.week ?? {};
        const goals = w.goals ?? [];
        const top = goals.length ? goals[goals.length - 1] : 0;
        const pct = top > 0 ? Math.min(100, (100 * (w.waves ?? 0)) / top) : 0;
        const members = w.members ?? [];
        const memberColumns = [
            { id: "name", label: "Member", width: "50%", render: m => <PlayerLink id={m.accountId} name={m.name} /> },
            { id: "waves", label: "Waves", width: "25%", align: "right" },
            { id: "donated", label: "Donated (gems)", width: "25%", align: "right" },
        ];
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Guild layer</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip size="small" label={`level ${x.level}`} />
                    <Chip size="small" label={`xp ${x.xp}`} />
                    <Chip size="small" label={`coins ${x.coins}`} />
                    <Chip size="small" label={`war coins ${x.warCoins}`} />
                    <Chip size="small" label={`xp bank ${x.xpBank}`} />
                    <Chip size="small" label={`slots ${x.cap} · officers ${x.officerCap}`} />
                    {x.title ? <Chip size="small" label={`title "${x.title}"`} /> : null}
                    {x.minChapter ? <Chip size="small" label={`chapter ${x.minChapter}+`} /> : null}
                    <Chip size="small" variant="outlined" label={`seq ${x.seq}`} />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Week {w.id} (ends {date(w.endsAt)}): {w.waves ?? 0} waves · goals {goals.join(" / ") || "—"} · reached {MILESTONES[w.reached] ?? w.reached}
                    {w.leftWaves ? ` · ${w.leftWaves} from members who left` : ""}
                </Typography>
                <Box sx={{ position: "relative", mt: 0.5 }}>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 10, borderRadius: 1 }} />
                    {goals.map((gl, i) => <Box key={i} sx={{ position: "absolute", top: -2, bottom: -2, left: `${top > 0 ? (100 * gl) / top : 0}%`, width: "2px", bgcolor: "text.primary" }} />)}
                </Box>
                {members.length > 0 && <DataTable sx={{ ...FIXED_TABLE, mt: 1 }} columns={memberColumns} rows={members} getRowKey={m => m.accountId} />}
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Upgrades: {Object.entries(x.upgrades ?? {}).map(([k, v]) => `${k} ${v}`).join(", ") || "none"} · showcase: {(x.shop?.weekly ?? []).join(", ") || "—"} · owned {(x.owned ?? []).length}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Muted: {muted == null ? "…" : muted.length === 0 ? "nobody" : muted.map(m => <Chip key={m.accountId} size="small" sx={{ mr: 0.5 }} label={`${m.name || m.accountId}${m.member ? "" : " (left)"}`} />)}
                </Typography>
            </Box>
        );
    }

    // One operator verb on the open guild, then the guild and the list again
    act = (parts, description, success, payload) => {
        this.setState({ busy: true });
        return guilds(description, [...parts, "--branch", this.state.branch], { successMessage: success, ...(payload ? { payload } : {}) })
            .then(() => { this.setState({ action: null }); this.load(); if (this.state.guild?.id) this.open(this.state.guild.id); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    disband = (g) => this.setState({ action: {
        title: `Disband ${g.name}?`, confirmText: "Disband",
        text: "Every member leaves (the rejoin cooldown applies), the name is free again and the guild channel stays readable for 30 days.",
        run: () => this.act(["disband", g.id], "Disbanding the guild", "Guild disbanded"),
    }});

    rename = (g) => this.setState({ action: {
        title: `Rename ${g.name}`, confirmText: "Rename", input: { label: "New name", initial: g.name },
        text: "The operator's rename: the name rules apply (3-20 characters, unique on the branch).",
        run: (name) => this.act(["rename", g.id, name], "Renaming the guild", "Guild renamed"),
    }});

    transfer = (g, m) => this.setState({ action: {
        title: `Make ${m.name || m.accountId} the leader of ${g.name}?`, confirmText: "Transfer",
        text: "The member becomes the leader; the current leader becomes an officer.",
        run: () => this.act(["transfer", g.id, m.accountId], "Transferring the leadership", "Leadership transferred"),
    }});

    kick = (g, m) => this.setState({ action: {
        title: `Kick ${m.name || m.accountId} from ${g.name}?`, confirmText: "Kick",
        text: "The member leaves the guild (a guild.kicked line in its channel) and cannot rejoin a guild during the cooldown.",
        run: () => this.act(["kick", g.id, m.accountId], "Kicking the member", "Member kicked"),
    }});

    setExt = (g) => this.setState({ action: {
        title: `The title's data of ${g.name}`, confirmText: "Save",
        input: { label: "ext (JSON object)", initial: typeof g.ext === "string" ? g.ext || "{}" : JSON.stringify(g.ext ?? {}, null, 2), multiline: true },
        text: "The title's own systems read this object (the base never does). It replaces the whole ext.",
        run: (text) => {
            let body;
            try { body = JSON.parse(text); } catch (e) { window.alert(`Not JSON: ${e.message}`); return; }
            return this.act(["set-ext", g.id], "Saving the ext", "Ext saved", body);
        },
    }});

    renderGuild(g) {
        const edit = canEdit("guilds");
        const roster = g.roster ?? [];
        const memberColumns = [
            { id: "name", label: "Member", width: "30%", render: m => <PlayerLink id={m.accountId} name={m.name} /> },
            { id: "role", label: "Role", width: "12%", render: m => roleName(m.role) },
            { id: "joinedAt", label: "Joined", width: "18%", render: m => date(m.joinedAt) },
            { id: "seen", label: "Seen", width: "20%", render: m => m.online ? <Chip size="small" color="success" label="online" /> : date(m.lastSeen) },
            { id: "actions", label: "", width: "20%", align: "right", render: m => edit && roleName(m.role) !== "leader" ? (
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button size="small" onClick={() => this.transfer(g, m)} disabled={this.state.busy || !!g.disbandedAt}>Make leader</Button>
                    <Button size="small" color="error" onClick={() => this.kick(g, m)} disabled={this.state.busy || !!g.disbandedAt}>Kick</Button>
                </Box>) : "" },
        ];
        const askColumns = [
            { id: "account", label: "Account", width: "50%", render: a => <PlayerLink id={a.accountId ?? a.account} name={a.name} /> },
            { id: "by", label: "By", width: "25%", render: a => a.by ? <PlayerLink id={a.by} /> : "" },
            { id: "at", label: "At", width: "25%", render: a => date(a.at ?? a.joinedAt) },
        ];
        return (
            <Paper sx={{ p: 2, mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                    <Box>
                        <Typography variant="h6">{g.name} {g.tag ? <Chip size="small" label={g.tag} sx={{ ml: 1 }} /> : null} {g.disbandedAt ? <Chip size="small" color="error" label={`disbanded ${date(g.disbandedAt)}`} sx={{ ml: 1 }} /> : null}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {g.id} · {g.branch ?? this.state.branch} · {JOIN_MODES[g.joinMode] ?? g.joinMode} · {g.language || "—"} · {g.members ?? roster.length}/{g.cap ?? "?"} members · leader <PlayerLink id={g.leader} /> · created {date(g.createdAt)}{g.updatedAt ? ` · updated ${date(g.updatedAt)}` : ""}
                        </Typography>
                        {g.description && <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{g.description}</Typography>}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {edit && !g.disbandedAt && <Button size="small" onClick={() => this.rename(g)} disabled={this.state.busy}>Rename</Button>}
                        {edit && <Button size="small" onClick={() => this.setExt(g)} disabled={this.state.busy}>Set ext</Button>}
                        {edit && !g.disbandedAt && <Button size="small" color="error" onClick={() => this.disband(g)} disabled={this.state.busy}>Disband</Button>}
                        <Button size="small" onClick={() => this.setState({ guild: null, layer: null, muted: null })}>Close</Button>
                    </Box>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Roster ({roster.length})</Typography>
                <DataTable sx={FIXED_TABLE} columns={memberColumns} rows={roster} getRowKey={m => m.accountId} />
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Requests ({(g.requests ?? []).length}) · Invites ({(g.invites ?? []).length})</Typography>
                {(g.requests ?? []).length + (g.invites ?? []).length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={askColumns} rows={[...(g.requests ?? []), ...(g.invites ?? [])]} getRowKey={(a, i) => `${a.accountId ?? a.account}-${i}`} />
                    : <Typography variant="body2" color="text.secondary">None pending.</Typography>}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>ext</Typography>
                <Typography variant="body2" component="pre" sx={{ m: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {typeof g.ext === "string" ? g.ext || "(none)" : JSON.stringify(g.ext ?? null, null, 2)}
                </Typography>
                {g.channel && <Typography variant="caption" color="text.secondary">channel {g.channel} (Chat reports → Lines)</Typography>}
                {this.renderLayer()}
            </Paper>
        );
    }

    render() {
        const { branch, query, list, loading, guild, busy, action } = this.state;
        const columns = [
            { id: "name", label: "Name", width: "24%", render: g => (
                <Typography component="span" variant="body2" sx={{ color: "primary.main", cursor: "pointer", wordBreak: "break-word" }} onClick={() => this.open(g.id)}>{g.name}</Typography>) },
            { id: "tag", label: "Tag", width: "8%" },
            { id: "members", label: "Members", width: "10%", align: "right", render: g => g.cap ? `${g.members}/${g.cap}` : g.members },
            { id: "joinMode", label: "Join", width: "12%", render: g => JOIN_MODES[g.joinMode] ?? g.joinMode },
            { id: "language", label: "Lang", width: "8%" },
            { id: "leader", label: "Leader", width: "18%", render: g => <PlayerLink id={g.leader} name={g.leaderName} /> },
            { id: "createdAt", label: "Created", width: "20%", render: g => date(g.createdAt) },
        ];
        return (
            <PageContainer>
                <SectionHeader title="Guilds">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · the generic guild base</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={v => this.setState({ branch: v, guild: null }, this.load)} />
                    <TextField label="Name starts with" size="small" value={query} onChange={e => this.setState({ query: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.load(); }} sx={{ width: 240 }} />
                    <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                </SelectorBar>
                {list && list.length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={columns} rows={list} getRowKey={g => g.id} />
                    : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{loading || !list ? "Loading…" : "No guild on this branch."}</Typography></Paper>}
                {guild && this.renderGuild(guild)}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
